/**
 * SEOHead — emit per-page SEO meta tags via React 19's native head hoisting.
 *
 * Centralizes the canonical / og / twitter card boilerplate so every public
 * page consistently advertises itself to crawlers and social scrapers.
 *
 * No external deps — react-helmet is intentionally NOT used (it's
 * incompatible with React 19).
 *
 * Usage:
 *   <SEOHead
 *     title="All Episodes"
 *     description="Every Golf Nuts conversation, listed in one place."
 *     path="/episodes"
 *     ogType="website"          // "article" for content pages, default "website"
 *     image="https://..."       // optional og:image
 *   />
 */
import React from 'react';

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || '';

export const SEOHead = ({
  title,
  description,
  path = '',
  ogType = 'website',
  image,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | Golf Nuts` : 'Golf Nuts';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const canonical = PUBLIC_SITE_URL ? `${PUBLIC_SITE_URL}${cleanPath}` : null;

  return (
    <>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title || 'Golf Nuts'} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {image && <meta property="og:image" content={image} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title || 'Golf Nuts'} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
    </>
  );
};

export default SEOHead;
